import React, { useContext, useEffect, useState, useMemo } from 'react';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import PropTypes from 'prop-types';
import { NumericFormat } from 'react-number-format';
import { useFormik, Form, FormikProvider } from 'formik';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  InputAdornment,
  Grid,
  Divider,
  Chip,
  Tab
} from '@mui/material';
import { TabContext, TabList, TabPanel, LoadingButton } from '@mui/lab';
// contexts
import { useAuth } from '../../../../contexts/Auth';
import { SettingsContext } from '../../../../contexts/Settings';
import { TicketsContext } from '../../../../contexts/Tickets';
// services
import { createBatch, updateBatch } from '../../../../services/Ticket';
import { updateInventoryMany } from '../../../../services/Inventory';

dayjs.extend(utc);
dayjs.extend(timezone);

TicketBatchesFormDialog.propTypes = {
  openTicketBatchFormDialog: PropTypes.bool.isRequired,
  setOpenTicketBatchFormDialog: PropTypes.func.isRequired,
  ticket: PropTypes.object,
  batch: PropTypes.object
};

export default function TicketBatchesFormDialog({
  openTicketBatchFormDialog,
  setOpenTicketBatchFormDialog,
  ticket,
  batch
}) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { setTickets } = useContext(TicketsContext);

  const initialBatchValues = useMemo(
    () => ({
      name: '',
      expiresIn: null,
      tmpExpiresIn: null,
      discount: '',
      limit: '',
      translation: {
        pt: {
          name: ''
        }
      }
    }),
    []
  );

  const [batchValues, setBatchValues] = useState(initialBatchValues);

  useEffect(() => {
    if (batch?._id) {
      setBatchValues({
        name: batch.name,
        expiresIn: new Date(batch.expiresIn).toLocaleString('en', {
          timeZone: 'Europe/Lisbon'
        }),
        tmpExpiresIn: new Date(batch.expiresIn).toLocaleString('en', {
          timeZone: 'Europe/Lisbon'
        }),
        discount: batch.discount,
        limit: batch.limit,
        translation: batch.translation
      });
    } else {
      setBatchValues(initialBatchValues);
    }
  }, [batch, initialBatchValues]);

  // TODO: Validar data com Yup: formato e se é maior que hoje, mes tem que ser de 1 a 12, etc...
  const BatchSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    expiresIn: Yup.date().nullable().required('Expires in is required'),
    discount: Yup.number().integer().positive().required('Discount is required'),
    limit: Yup.number().integer().positive().required('Limit is required'),
    translation: Yup.object({
      pt: Yup.object({
        name: Yup.string()
      })
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: batchValues,
    validationSchema: BatchSchema,
    onSubmit: async (values, actions) => {
      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        values.expiresIn = dayjs(values.expiresIn).tz('Europe/Lisbon', true).toISOString();

        const payload = {
          name: values.name,
          expiresIn: values.expiresIn,
          discount: values.discount,
          limit: values.limit,
          translation: {
            pt: {
              name: values.translation.pt.name
            }
          }
        };

        // TODO: Atualizar usando kafka (provisório)
        const payloadServices = {
          ticket: {
            _id: ticket._id,
            name: ticket.name,
            reference: ticket.reference,
            limit: ticket.limit,
            price: ticket.price,
            rule: {
              name: values.name,
              expiresIn: values.expiresIn,
              discount: values.discount,
              limit: values.limit,
              translation: {
                pt: {
                  name: values.translation.pt.name
                }
              }
            }
          }
        };

        if (batch?._id) {
          await updateBatch({
            headers,
            payload,
            eventId: ticket.event._id,
            sectionId: ticket.section._id,
            ticketId: ticket._id,
            batchId: batch._id
          });

          payloadServices.ticket.rule._id = batch._id;
          payloadServices.ticket.rule.action = 'update';

          setTickets((tickets) => {
            tickets[ticket.section._id] = tickets[ticket.section._id].map((currentTicket) => {
              if (currentTicket._id === ticket._id) {
                currentTicket.rules = currentTicket.rules.map((currentRule) => {
                  if (currentRule._id === batch._id) {
                    return {
                      ...currentRule,
                      name: values.name,
                      expiresIn: values.expiresIn,
                      discount: values.discount,
                      limit: values.limit,
                      translation: {
                        pt: {
                          name: values.translation.pt.name
                        }
                      }
                    };
                  }

                  return currentRule;
                });
              }
              return currentTicket;
            });
            return tickets;
          });
        } else {
          const newBatch = await createBatch({
            headers,
            payload,
            eventId: ticket.event._id,
            sectionId: ticket.section._id,
            ticketId: ticket._id
          });

          payloadServices.ticket.rule._id = newBatch._id;
          payloadServices.ticket.rule.action = 'create';

          setTickets((tickets) => {
            tickets[ticket.section._id] = tickets[ticket.section._id].map((currentTicket) => {
              if (currentTicket._id === ticket._id) {
                currentTicket.rules.push(newBatch);
              }
              return currentTicket;
            });
            return tickets;
          });
        }

        await updateInventoryMany({ headers, payload: payloadServices });
        handleClose();
        actions.resetForm();
      } catch (err) {
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const handleClose = () => {
    setOpenTicketBatchFormDialog(false);
  };

  const { values, errors, touched, getFieldProps, handleSubmit, isSubmitting, setFieldValue } =
    formik;

  return (
    <Dialog open={openTicketBatchFormDialog} fullWidth scroll="body" maxWidth="sm" sx={{ m: -2 }}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>Create/Edit Batch</DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
            <Box sx={{ mt: 1 }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="[EN] Name*"
                  placeholder="i.e. Batch 1"
                  {...getFieldProps('name')}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expires In*"
                    value={values.tmpExpiresIn || null}
                    name="expiresIn"
                    showToolbar={false}
                    onChange={(date) => {
                      setFieldValue(`tmpExpiresIn`, date);
                      // PS: Aqui não é para coloca timezone Lisbon
                      // Pois pega o date/timezone atual e depois converte
                      const expiresIn = new Date(date);
                      expiresIn.setHours(23, 59, 59, 999);
                      setFieldValue(`expiresIn`, dayjs(expiresIn).tz('Europe/Lisbon', true));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...getFieldProps('expiresIn')}
                        helperText={
                          (touched.expiresIn && errors.expiresIn) || params?.inputProps?.placeholder
                        }
                        {...params}
                      />
                    )}
                  />
                </LocalizationProvider>

                <Grid container>
                  <Grid item xs={6} pr={1}>
                    <NumericFormat
                      fullWidth
                      label="Discount (0 - 100%)*"
                      {...getFieldProps('discount')}
                      error={Boolean(touched.discount && errors.discount)}
                      helperText={touched.discount && errors.discount}
                      decimalScale={0}
                      customInput={TextField}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} pl={1}>
                    <NumericFormat
                      fullWidth
                      label="Limit*"
                      {...getFieldProps('limit')}
                      error={Boolean(touched.limit && errors.limit)}
                      helperText={touched.limit && errors.limit}
                      decimalScale={0}
                      customInput={TextField}
                    />
                  </Grid>
                </Grid>

                <Divider>
                  <Chip label="Translations" size="small" />
                </Divider>

                <Box sx={{ width: '100%', typography: 'body1', marginTop: '0 !important' }}>
                  <TabContext value="pt">
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <TabList>
                        <Tab label="PT" value="pt" />
                      </TabList>
                    </Box>
                    <TabPanel value="pt">
                      <Stack spacing={2}>
                        <TextField
                          fullWidth
                          label="[PT] Name"
                          variant="filled"
                          {...getFieldProps(`translation.pt.name`)}
                          error={Boolean(
                            touched.translation?.pt?.name && errors.translation?.pt?.name
                          )}
                          helperText={touched.translation?.pt?.name && errors.translation?.pt?.name}
                        />
                      </Stack>
                    </TabPanel>
                  </TabContext>
                </Box>
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button size="large" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}>
              Save
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
